import React from 'react';
import { createRoot } from 'react-dom/client';
import { AppController } from './AppController';
import { ButtonController } from './ButtonController';
import { ShopServiceFactory } from '../tools/ShopServiceFactory';
import EventEmitter from 'eventemitter3';
import shopService from '../services/ShopService';
import storageService from '../services/StorageService';
import { GenericService } from '../services';
import RenderUtil from '../util/RenderUtil';
import log from '../services/LogService';
import widgetEvents from './Events';

export const emitter = new EventEmitter();

const onRootElChange = (mutationList, observer) => {
  for (const mutation of mutationList) {
    if (mutation.type === "childList") {
      log.info("A child node has been added or removed.");
    } else if (mutation.type === "attributes" && mutation.attributeName === "data-measurement-id") {
      log.info(`The ${mutation.attributeName} attribute was modified.`);
      const measurementId = mutation.target.getAttribute(mutation.attributeName);
      const articleNumber = mutation.target.dataset.articleNumber;
      const processProfileMeasurement = () => emitter.emit(widgetEvents.onProfileMeasurementSet, { articleNumber, measurementId });

      if (shopService.hasShopSetting()) {
        processProfileMeasurement();
        return;
      }

      log.info('waiting for driver to be initialized to process profile measurement');
      window.addEventListener('fpt-initialized', processProfileMeasurement, { once: true });

    }
  }
};

export const FootprintTechDriver = {
  render: (config) => {

    log.info('rendering widgets . . .');

    const customShopService = ShopServiceFactory.create();

    if (customShopService instanceof GenericService) {
      log.info('using Generic Service');
    } else {
      log.info('using Custom Service');
      shopService.setCustomService(customShopService);
    }

    const articleList = [];

    let apiKey = shopService.getApiKey(config);

    // set API key from custom service (deprecated)
    // or script element
    if (apiKey) {
      shopService.setApiKey(apiKey);
    }

    const renderButtonFromHTMLElement = (el:HTMLElement) => {
      const article = shopService.getArticleData(el);
      articleList.push(article);
      const root = createRoot(el);

      // set API key from widget. This is deprecated
      if (!apiKey) {
        apiKey = shopService.getApiKey(el.dataset);
        shopService.setApiKey(apiKey);
      }

      if ('measurementId' in el.dataset && el.dataset.measurementId) {
        const { measurementId } = el.dataset;
        log.info('got measurement', measurementId);
        storageService.setMeasurementId(measurementId);
      }

      // Create an observer instance linked to the callback function
      const observer = new MutationObserver(onRootElChange);

      // Start observing the target node for configured mutations
      observer.observe(el, { attributes: true, childList: false, subtree: false });

      root.render(
        <ButtonController emitter={emitter} articleData={article} />
      );
    };

    document.querySelectorAll('[data-widget=fpt-button]')
      .forEach(renderButtonFromHTMLElement);

    // this is deprecated. Does not work with data-widget attribute
    const legacyWidget = document.querySelector('#footprinttech-button');
    if (legacyWidget) {
      const article = shopService.getArticleData(legacyWidget as HTMLElement);
      articleList.push(article);
      renderButtonFromHTMLElement(legacyWidget as HTMLElement);
    }

    if (!apiKey) {
      throw new Error('No API key found');
    }

    const modalEl = RenderUtil.appendModalEl();

    const appRoot = createRoot(modalEl);

    appRoot.render(
          <AppController emitter={emitter} articleList={articleList} />
    );
  },

  renderModal: () => {
    log.info('Headless mode initilizing');
    const el = RenderUtil.appendHeadlessModalEl();
    const root = createRoot(el);

    const articleList = [];
    const widget = document.querySelector('[data-widget=fpt-modal]');
    const article = shopService.getArticleData(widget as HTMLElement);
    articleList.push(article);

    root.render(
        <AppController emitter={emitter} headlessEnabled="true" articleList={articleList} />
    )
  },

  isCheckoutPage: () => {
    const Service = ShopServiceFactory.getShopService();
    return Service.isCheckout();
  },

  initBanner: (bannerId:string) => {
    log.info('has banner');
    // const el = document.getElementById(bannerId);
    const el = RenderUtil.appendModalEl();
    const root = createRoot(el);

    root.render(
        <AppController emitter={emitter} bannerId="footprinttech-banner" />
    )
  },

  handleCheckout: () => {
    log.info('checkout called');
    const Service = ShopServiceFactory.getShopService();
    return Service.handleCheckout();
  },
};
